import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

import { View, Text, Button } from "../base";
import { Icon } from "../base/Icon";
import { IconButton } from "../base/IconButton";
import { useAuth } from "../../hooks/useAuth";
import { useOnboardingState } from "./useOnboardingState";
import { OnboardingPanel } from "./OnboardingPanel";
import { PANEL_TITLE } from "./onboardingSteps";
import { ONBOARDING_DRAWER_OPEN_EVENT, ONBOARDING_DRAWER_CLOSE_EVENT } from "./onboardingEvents";

const getDismissedKey = (userId: string) => `onboarding_drawer_dismissed_${userId}`;

export const OnboardingDrawer = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const { completedCount, totalSteps, allCompleted } = useOnboardingState(userId);

  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    if (!userId) return false;
    try {
      return localStorage.getItem(getDismissedKey(userId)) === "true";
    } catch {
      return false;
    }
  });

  // Re-check dismissed state when userId changes
  useEffect(() => {
    if (!userId) return;
    try {
      setIsDismissed(localStorage.getItem(getDismissedKey(userId)) === "true");
    } catch {
      setIsDismissed(false);
    }
  }, [userId]);

  // Listen for external open events (e.g. from MissionControlPage button).
  // Also clears dismissed state so the floating trigger reappears.
  useEffect(() => {
    const handler = () => {
      setIsOpen(true);
      if (userId) {
        try {
          localStorage.removeItem(getDismissedKey(userId));
        } catch {
          // silent
        }
        setIsDismissed(false);
      }
    };
    window.addEventListener(ONBOARDING_DRAWER_OPEN_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_DRAWER_OPEN_EVENT, handler);
  }, [userId]);

  // Listen for close events (e.g. after in-app navigation from a step action)
  useEffect(() => {
    const handler = () => setIsOpen(false);
    window.addEventListener(ONBOARDING_DRAWER_CLOSE_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_DRAWER_CLOSE_EVENT, handler);
  }, []);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isOpen]);

  const handleDismiss = useCallback(() => {
    if (!userId) return;
    try {
      localStorage.setItem(getDismissedKey(userId), "true");
    } catch {
      // silent
    }
    setIsDismissed(true);
  }, [userId]);

  if (!userId) return null;

  const showTrigger = !allCompleted && !isDismissed;

  return (
    <>
      {/* Right-edge tab trigger */}
      {showTrigger && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed right-0 bottom-24 z-40 px-1.5 py-2 rounded-l-lg bg-brand text-white shadow-lg hover:shadow-xl hover:pr-2.5 transition-all duration-200"
        >
          <Text className="text-[11px] font-medium text-white">
            {completedCount}/{totalSteps}
          </Text>
        </button>
      )}

      {/* Drawer + backdrop */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-50"
            onClick={() => setIsOpen(false)}
          />

          {/* Drawer panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            role="dialog"
            aria-modal="true"
            aria-label="Onboarding checklist"
            className="fixed top-0 right-0 h-full w-[340px] max-w-[100vw] bg-white dark:bg-slate-900 shadow-2xl z-50 flex flex-col"
          >
            {/* Drawer header */}
            <View className="flex flex-row items-center justify-between px-3 py-2.5 border-b border-gray-200 dark:border-gray-800">
              <View className="flex flex-row items-center gap-2">
                <Text className="text-sm font-medium text-gray-900 dark:text-white">{PANEL_TITLE}</Text>
                <Text className="text-xs text-gray-400 dark:text-gray-500">
                  {completedCount}/{totalSteps}
                </Text>
              </View>
              <IconButton
                variant="ghost"
                icon="close"
                onClick={() => setIsOpen(false)}
                aria-label="Close onboarding drawer"
              />
            </View>

            {/* Drawer body */}
            <View className="flex-1 overflow-y-auto p-3">
              <OnboardingPanel />
            </View>

            {/* Drawer footer */}
            {!allCompleted && (
              <View className="px-3 py-2.5 border-t border-gray-200 dark:border-gray-800">
                <Button
                  variant="neutral"
                  size="sm"
                  outline
                  onClick={() => {
                    handleDismiss();
                    setIsOpen(false);
                  }}
                  className="w-full justify-center"
                >
                  Skip onboarding
                </Button>
              </View>
            )}
          </motion.div>
        </>
      )}
    </>
  );
};
